import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import Negative from './Negative';
import ImageDesc from './ImageDesc';
import config from '../../config';
import {
  Form,
  Input,
  notification,
  Row,
  Col,
  Button,
  Breadcrumb,
  Typography,
  Select,
  DatePicker,
  Upload,
  Tabs,
  message,
  Spin,
  Divider,
} from 'antd';
import {
  formItemLayout,
  tailFormItemLayout,
  unexpectedError,
  showUploadFileList,
  showFilesList,
  handleFileChange,
  formatDate,
  parseDate,
  beforeUpload,
} from '../../common_functions';
import UploadTrigger from '../../common_functions/UploadTrigger';
import UploadedCheck from '../../common_functions/UploadedCheck';

// Tabs
const CreateObject = props => {
  const { section, section_2, id } = useParams();
  const [collections, setCollections] = useState([]);
  const [artist, setArtist] = useState([]);
  const [title, setTitle] = useState('Create Object');
  const [loading, setLoading] = useState(false);
  const [sectionID, setsectionID] = useState(null);
  const [TabActiveKey, setTabActiveKey] = useState('1');
  const [allObjectData, setAllObjectData] = useState();
  const [negativeID, setNegativeID] = useState(false);
  const [authenticityConfidence, setAuthenticityConfidence] = useState([]);
  const [supportData, setSupportData] = useState([]);
  const [workName, setWorkName] = useState(false);
  const [locations, setLocations] = useState([]);
  const formRef = useRef(false);
  const [objID, setObjID] = useState(null);
  const [filesState, setFileState] = useState({
    provenance_doc: [],
    authenticity_file: [],
  });
  const handleStateChange = useCallback((tab, tabTitle) => {
    setTabActiveKey(tab);
    setTitle(tabTitle);
  }, []);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        formRef.current.setFieldsValue({ object_type: 'negative' });
        if (id && section_2 === 'collection') {
          formRef.current.setFieldsValue({ collections: Number(id) });
        }
        //get support table data
        const { data: supData } = await props.getItems('support_table', {
          fields: ['id', 'type', 'name', 'fields'],
          limit: -1,
          filters: {
            fields: {
              _in: ['record_type', 'object_type', 'work_output', 'classification', 'catalog_level'],
            },
          },
        });

        setSupportData(supData);
        //set default values
        formRef.current.setFieldsValue({
          catalog_level: supData.find(el => el.fields === 'catalog_level')?.id || undefined,
          classification: supData.find(el => el.type === 'classification' && el.name.toLowerCase() === 'photograph')?.id || undefined,
          work_type: supData.find(el => el.fields === 'work_output' && el.name.toLowerCase() === 'negative')?.id || undefined,
          obj_type: supData.find(el => el.type === 'object type' && el.name.toLowerCase() === 'individual object')?.id || undefined,
          record_type:
            supData.find(
              el => el.type === 'record type' && (el.name.toLowerCase() === 'movable work' || el.name.toLowerCase() === 'moveable work')
            )?.id || undefined,
        });
        let { data: objectLocations } = await props.getItems('location', {
          fields: ['id', 'name'],
          sort: 'name',
          limit: -1,
        });
        setLocations(objectLocations);
        //get autenticity confidence
        const { data: authenticityData } = await props.getItems('authenticity_confidence', {
          fields: ['id', 'confidence'],
          sort: '-value',
          limit: -1,
        });
        setAuthenticityConfidence(authenticityData);
        /**Get artist name*/
        let { data: artistFullName } = await props.getItems('Artist', {
          fields: ['id', 'First_Name', 'last_name'],
          sort: '-id',
          limit: -1,
        });
        setArtist(artistFullName);
        // get collections list
        let { data: colRespData } = await props.getItems('collections', {
          fields: ['id', 'name', 'folderID'],
          sort: '-id',
          limit: -1,
          filters: {
            associated_to: {
              _in: ['negative', 'both'],
            },
          },
        });
        setCollections(colRespData);
        if (section_2 === 'edit' && id) {
          let { data } = await props.getItem('collection_obj', id, {
            fields: [
              '*,provenance_doc.id,provenance_doc.filename_download,authenticity_file.id,authenticity_file.filename_download,work_type.name,work_type.id,taag_image,common_reference,accepted_date',
            ],
            filter: { object_type: { _eq: 'negative' } },
          });
          setFileState({
            provenance_doc: showUploadFileList(data?.provenance_doc?.id, data?.provenance_doc?.filename_download),
            authenticity_file: showUploadFileList(data.authenticity_file?.id, data?.authenticity_file?.filename_download),
          });
          data.provenance_doc = data?.provenance_doc?.id;
          data.authenticity_file = data?.authenticity_file?.id;
          getObjectLocationDetails(data.collections);
          data.acquisition_date = parseDate(data?.acquisition_date);
          data.accepted_date = parseDate(data?.accepted_date);
          data.creator = data?.creator ? Number(data.creator) : null;
          setNegativeID(data.negative);
          setAllObjectData(data);
          setsectionID(data.id);
          setObjID(data.id);
          setTitle('Edit Object');
          setWorkName(data.work_type.name);
          formRef.current.setFieldsValue(data);
          formRef.current.setFieldsValue({ work_type: data.work_type.id });
          !!localStorage.getItem('negActive') && handleTabChange(localStorage.getItem('negActive'));
        }
        setLoading(false);
      } catch (error) {
        if (error?.errors?.[0]?.extensions?.code === 'FORBIDDEN') {
          backButton();
          return;
        }
        unexpectedError(error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [section, id]);

  // Change Title according to active tab
  let handleTabChange = e => {
    localStorage.removeItem('negActive');
    if (e == 2) {
      setTabActiveKey('2');
      setTitle('Negative');
    } else if (e == 3) {
      setTabActiveKey('3');
      setTitle('Image Description');
    } else {
      setTabActiveKey('1');
      setTitle(!!id ? 'Edit Object' : 'Create Object');
    }
  };

  let onFinish = async values => {
    setLoading(true);
    const fd = new FormData();
    values.acquisition_date = formatDate(values?.acquisition_date);
    values.accepted_date = formatDate(values?.accepted_date);
    Object.entries(values).forEach(([key, value]) => {
      if (value?.fileList?.[0].originFileObj) {
        fd.append(key, value.fileList[0].originFileObj);
      } else {
        fd.append(key, value);
      }
    });
    props
      .collectionObject(fd, 'file/collection_obj', sectionID)
      .then(
        async response => {
          if (response) {
            setLoading(false);
            if (response.error) {
              notification['error']({
                message: response.error,
              });
            } else {
              setTitle('Edit Object');
              notification['success']({
                message: 'Object saved successfully!',
                duration: 2,
              });
            }
            let data = response.data;
            data.acquisition_date = parseDate(data?.acquisition_date);
            data.accepted_date = parseDate(data?.accepted_date);
            data.creator = data?.creator ? Number(data.creator) : null;
            const provenanceDocName = values?.provenance_doc?.file?.name ?? filesState?.provenance_doc?.[0]?.name;
            const authenticityFileName = values?.authenticity_file?.file?.name ?? filesState?.authenticity_file?.[0]?.name;
            setFileState({
              provenance_doc: showUploadFileList(data?.provenance_doc, provenanceDocName),
              authenticity_file: showUploadFileList(data?.authenticity_file, authenticityFileName),
            });
            formRef.current.resetFields(['provenance_doc', 'authenticity_file']);
            formRef.current.setFieldsValue(data);
            supportData.filter(function (el) {
              return el.id == data.work_type && setWorkName(el.name);
            });

            setAllObjectData(data);
            setsectionID(data.id);
            setTabActiveKey('2');
            if (!objID) {
              await props.updatePermissionData(response.data.id);
            }
          }
        },
        errorReponse => {
          setLoading(false);
          if (errorReponse.errors !== undefined) {
            for (var i = 0; i < errorReponse.errors.length; i++) {
              var errorObj = errorReponse.errors[i];
              var errorField = errorObj.extensions.field;
              var errorFieldMessage = errorObj.message;
              notification['error']({
                message: errorFieldMessage,
              });
              formRef.current.setFields([
                {
                  name: errorField,
                  errors: [errorFieldMessage],
                },
              ]);
            }
          } else {
            setLoading(false);
            notification['error']({
              message: 'Not Saved!!',
            });
          }
        }
      )
      .catch(error => {
        console.log(error, 'errorerrorerrorerror');
        setLoading(false);
        notification['error']({
          message: 'Not Saved!!',
        });
      });
  };
  let onFinishFailed = ({ errorFields }) => {
    formRef.current.scrollToField(errorFields[0].name);
  };
  let backButton = () => {
    setTimeout(() => props.history.push('/negatives/negative-objects', 1500));
  };
  let showButton = () => {
    return props.showButton(props.roleType, props.tabs_permissions.negatives, objID, props.prints_negatives_obj);
  };
  const getObjectLocationDetails = async collectionID => {
    setLocations([]);
    if (!!collectionID) {
      let { data: objectLocations } = await props.getItems('location', {
        fields: ['id', 'name'],
        sort: 'name',
        limit: -1,
        filters: { collections: { _eq: collectionID } },
      });
      setLocations(objectLocations);
    }
  };
  return (
    <Spin spinning={loading} size="large">
      <Row>
        <Col className="app-breadcrumb-col">
          <Breadcrumb separator="/" items={[{ title: "Negatives", href: "/negatives/negative-objects" }, { title: "Negative Objects", href: "/negatives/negative-objects" }, { title: title }]} />
        </Col>
        <Col className="white-paper">
          <Row gutter={24} className="page-header-section" align="middle">
            <Col xs={12}>
              <Typography.Title level={4} style={{ margin: 0, textTransform: 'capitalize' }}>
                {!!allObjectData ? `Edit Object : ${workName}, ${allObjectData.title}, ${allObjectData.acquisition}` : 'Create Object'}
              </Typography.Title>
            </Col>
            <Col xs={12} justify="end"></Col>
          </Row>
          <hr />
          {/* Tabs start here */}
          <Tabs defaultActiveKey={TabActiveKey} activeKey={TabActiveKey} onChange={handleTabChange} items={[{
  label: section_2 === 'edit' || !!allObjectData ? 'Edit Object' : 'Create Object',
  key: "1",
  children: <div className="artist">
                <Form ref={formRef} layout="vertical" {...formItemLayout} onFinish={onFinish} onFinishFailed={onFinishFailed}>
                  {collections.id ? <Row gutter={24}>
                      <Col xs={24} sm={12} md={12} lg={12}>
                        <Form.Item name="ID" label="ID">
                          <Input defaultValue={collections.id} disabled />
                        </Form.Item>
                      </Col>
                    </Row> : ''}
                  <Form.Item hidden={true} name="object_type">
                    <Input />
                  </Form.Item>
                  <Row gutter={24}>
                    <Col xs={24} sm={12} md={12} lg={12}>
                      <Form.Item name="collections" label="Collection" rules={[{
            required: true,
            message: 'Collection is required'
          }]}>
                        <Select showSearch allowClear placeholder="Select a Collection" optionFilterProp="children" filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())} disabled={section_2 == 'edit' || section_2 == 'collection' ? true : false} onChange={getObjectLocationDetails}>
                          {collections.map(el => <Select.Option key={el.id} value={el.id}>
                              {el.name}
                            </Select.Option>)}
                        </Select>
                      </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} md={12} lg={12}>
                      <Form.Item name="title" label="Collection Title (Title per Collection Record)" rules={[{
            whitespace: true,
            required: true,
            message: 'Collection title is required!'
          }]}>
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={24}>
                    <Col xs={24} sm={12} md={12} lg={12}>
                      <Form.Item name="acquisition" label="Collection Acquisition # / ID#" rules={[{
            whitespace: true,
            required: true,
            message: 'Collection acquisition is required!'
          }]}>
                        <Input />
                      </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} md={12} lg={12}>
                      <Form.Item name="acquisition_date" label="Collection Acquisition Date">
                        <DatePicker picker="year" />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Divider />
                  <Row gutter={24}>
                    <Col xs={24} sm={12} md={12} lg={12}>
                      <Form.Item name="provenance" label="Provenance" rules={[{
            required: true,
            message: 'Provenance is required!'
          }]}>
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={12} lg={12}>
                      <Form.Item name="provenance_doc" label={<div>
                            Provenance Documentation
                            <UploadedCheck uploaded={allObjectData?.provenance_doc} />
                          </div>}>
                        <Upload name="file" fileList={filesState.provenance_doc} className="create_object" accept={config.FileType.docType.toString()} beforeUpload={file => {
              return beforeUpload(file, 'docType');
            }} maxCount={1} multiple={false} onChange={e => {
              handleFileChange(e, setFileState, 'provenance_doc');
            }} showUploadList={showFilesList(allObjectData?.provenance_doc, 'Provenance Doc')}>
                          <UploadTrigger fileType="docType" />
                        </Upload>
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={24}>
                    <Col xs={24} sm={12} md={12} lg={12}>
                      <Form.Item name="authenticity_confidences" label="Authenticity Confidence" rules={[{
            required: true,
            message: 'Authenticity confidence is required'
          }]}>
                        <Select showSearch allowClear placeholder="Enter authenticity confidence" optionFilterProp="children" filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}>
                          {authenticityConfidence.map(el => <Select.Option key={el.id} value={el.id}>
                              {el.confidence}
                            </Select.Option>)}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={12} lg={12}>
                      <Form.Item name="authenticity_file" label={<div>
                            Authenticity Confidence Doc
                            <UploadedCheck uploaded={allObjectData?.authenticity_file} />
                          </div>}>
                        <Upload name="file" fileList={filesState.authenticity_file} className="create_object" accept={config.FileType.docType.toString()} beforeUpload={file => {
              return beforeUpload(file, 'docType');
            }} maxCount={1} multiple={false} onChange={e => {
              handleFileChange(e, setFileState, 'authenticity_file');
            }} showUploadList={showFilesList(allObjectData?.authenticity_file, 'Authenticity Confidence Doc')}>
                          <UploadTrigger fileType="docType" />
                        </Upload>
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={24}>
                    <Col xs={24} sm={12} md={12} lg={12}>
                      <Form.Item name="location" label="Object Location Detail">
                        <Select showSearch allowClear placeholder="Select object location" optionFilterProp="children" filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}>
                          {locations.map(data => {
                return <Select.Option key={data.id} value={data.id}>{data.name}</Select.Option>;
              })}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={12} lg={12}>
                      <Form.Item name="recorded_size" label="Recorded Size">
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Divider />
                  <Row gutter={24}>
                    <Col xs={24} sm={12} md={12} lg={12}>
                      <Form.Item name="creator" label="Artist / Creator">
                        <Select showSearch allowClear placeholder="Select Artist" optionFilterProp="children">
                          {!!artist && artist.map(val => <Select.Option key={val.id} value={val.id}>
                                {val.First_Name} {val.last_name}
                              </Select.Option>)}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={12} lg={12}>
                      <Form.Item name="secondary_artist" label="Secondary Artist / Creator">
                        <Select showSearch allowClear placeholder="Select Secondary Artist" optionFilterProp="children">
                          {!!artist && artist.map(val => <Select.Option key={val.id} value={val.id}>
                                {val.First_Name} {val.last_name}
                              </Select.Option>)}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={12} lg={12}>
                      <Form.Item name="catalog_level" label="Catalog Level" rules={[{
            required: true,
            message: 'Catalog Level is required'
          }]}>
                        <Select showSearch allowClear placeholder="Enter catalog level" optionFilterProp="children" filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}>
                          {supportData.map(el => el.type === 'catalog level' && <Select.Option key={el.id} value={el.id}>
                                  {el.name}
                                </Select.Option>)}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={12} lg={12}>
                      <Form.Item name="record_type" label="Record type" rules={[{
            required: true,
            message: 'Record type is required'
          }]}>
                        <Select showSearch allowClear placeholder="Enter record type" optionFilterProp="children" filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}>
                          {supportData.map(el => el.type === 'record type' && <Select.Option key={el.id} value={el.id}>
                                  {el.name}
                                </Select.Option>)}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={12} lg={12}>
                      <Form.Item name="classification" label="Classification" rules={[{
            required: true,
            message: 'Classification is required'
          }]}>
                        <Select showSearch allowClear placeholder="Enter catalog level" optionFilterProp="children" filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}>
                          {supportData.map(el => el.type === 'classification' && <Select.Option key={el.id} value={el.id}>
                                  {el.name}
                                </Select.Option>)}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={12} lg={12}>
                      <Form.Item name="obj_type" label="Object Type" rules={[{
            required: true,
            message: 'Object Type is required'
          }]}>
                        <Select showSearch allowClear placeholder="Select a object type" optionFilterProp="children" filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}>
                          {supportData.map(el => el.type === 'object type' && <Select.Option key={el.id} value={el.id}>
                                  {el.name}
                                </Select.Option>)}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={12} lg={12}>
                      <Form.Item name="work_type" label="Work Type, Output" rules={[{
            required: true,
            message: 'Work type, output is required'
          }]}>
                        <Select showSearch allowClear placeholder="Select work type" optionFilterProp="children" filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}>
                          {supportData.map(el => el.type === 'work type' && el.fields === 'work_output' && <Select.Option key={el.id} value={el.id}>
                                  {el.name}
                                </Select.Option>)}
                        </Select>
                      </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} md={12} lg={12}>
                      <Form.Item name="accepted_date" label="Accepted Date">
                        <DatePicker picker="year" placeholder="Enter year" />
                      </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} md={12} lg={12}>
                      <Form.Item name="taag_image" label="TAAG Image #">
                        <Input />
                      </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} md={12} lg={12}>
                      <Form.Item name="common_reference" label="Common Reference">
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item {...tailFormItemLayout}>
                    <Button style={{
          margin: '0 8px',
          backgroundColor: 'lightgrey',
          color: '#333',
          borderColor: '#333'
        }} onClick={backButton}>
                      Back
                    </Button>
                    {showButton()}
                  </Form.Item>
                </Form>
              </div>
}, {
  label: "Negative",
  disabled: !allObjectData,
  key: "2",
  children: <Negative roleType={props.roleType} user_permissions={props.user_permissions} setAllObjectData={setAllObjectData} collectionObject={allObjectData} history={props.history} negID={setNegativeID} onChange={handleStateChange} showButton={showButton} />
}, {
  label: "Image Description",
  disabled: !!negativeID ? false : true,
  key: "3",
  children: <ImageDesc roleType={props.roleType} user_permissions={props.user_permissions} collectionObject={allObjectData} negID={negativeID} history={props.history} onChange={handleStateChange} showButton={showButton} />
}]} />
          {/* Tabs End here */}
        </Col>
      </Row>
    </Spin>
  );
};

export default CreateObject;
